import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { notify } from '@nimling/components-nimtech'
import { deleteNotification, getNotifications, putNotificationRead, setStreamNotifications } from '@/lib/api'
import { useSettingsStore } from '@/stores/settings'
import type { Notification } from '@/types'

export type NotificationCategory = 'alerts' | 'annotations' | 'access' | 'general'

export type NotificationDelivery = 'toast' | 'badge' | 'silent'

export interface NotificationRule {
  object_type?: string
  object_id?: string
  type?: string
  delivery: NotificationDelivery
}

export const getNotificationCategory = (n: Notification): NotificationCategory => {
  if (n.type.startsWith('alert_')) return 'alerts'
  if (n.type === 'annotation') return 'annotations'
  if (n.type.startsWith('access_')) return 'access'
  return 'general'
}

const RECONNECT_MS = 5000

const HANDOVER_MS = 1000

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const connected = ref(false)
  const settingsStore = useSettingsStore()

  let source: EventSource | null = null
  let reconnect: number | null = null
  let handover: number | null = null
  let streamsLive = 0

  const rules = computed(() => ((settingsStore.me.notifications as { rules?: NotificationRule[] } | undefined)?.rules ?? []))

  const getDelivery = (n: Notification): NotificationDelivery => {
    for (const rule of rules.value) {
      if (rule.type && rule.type !== n.type) continue
      if (rule.object_type && rule.object_type !== n.object_type) continue
      if (rule.object_id && rule.object_id !== n.object_id) continue
      return rule.delivery
    }
    return 'toast'
  }

  const unread = computed(() => notifications.value.filter((n) => !n.read_at && getDelivery(n) !== 'silent').length)

  const getNotificationList = async () => {
    notifications.value = await getNotifications()
  }

  const closeStream = () => {
    if (reconnect != null) window.clearTimeout(reconnect)
    reconnect = null
    source?.close()
    source = null
    connected.value = false
  }

  const disconnect = () => {
    if (handover != null) window.clearTimeout(handover)
    handover = null
    closeStream()
  }

  const putNotification = (raw: string) => {
    let held: Notification
    try {
      held = JSON.parse(raw) as Notification
    } catch {
      return
    }
    const at = notifications.value.findIndex((n) => n.id === held.id)
    if (at >= 0) {
      notifications.value[at] = held
      return
    }
    notifications.value = [held, ...notifications.value]
    if (getDelivery(held) === 'toast') notify(held.type.endsWith('firing') ? 'error' : 'info', held.title, held.description)
  }

  const openStream = () => {
    closeStream()
    if (streamsLive > 0) return
    source = new EventSource('/api/notification', { withCredentials: true })
    source.onopen = () => {
      connected.value = true
      void getNotificationList().catch(() => undefined)
    }
    source.onmessage = (event) => putNotification(event.data)
    source.onerror = () => {
      closeStream()
      reconnect = window.setTimeout(openStream, RECONNECT_MS)
    }
  }

  const putStreamsLive = (live: number) => {
    streamsLive = live
    if (handover != null) window.clearTimeout(handover)
    handover = window.setTimeout(() => {
      handover = null
      if (streamsLive > 0) closeStream()
      else if (!source && reconnect == null) openStream()
    }, HANDOVER_MS)
  }

  const connect = () => {
    disconnect()
    setStreamNotifications(putNotification, putStreamsLive)
    putStreamsLive(streamsLive)
  }

  const putRead = async (id: string) => {
    const held = notifications.value.find((n) => n.id === id)
    if (!held || held.read_at) return
    await putNotificationRead(id)
    held.read_at = new Date().toISOString()
  }

  const putAllRead = async (ids: string[]) => {
    for (const id of ids) await putRead(id)
  }

  const deleteOne = async (id: string) => {
    await deleteNotification(id)
    notifications.value = notifications.value.filter((n) => n.id !== id)
  }

  const isSilenced = (n: Notification) => !!n.object_id && rules.value.some((r) => r.object_id === n.object_id && r.delivery === 'silent')

  const putSilenced = (n: Notification, silenced: boolean) => {
    if (!n.object_id) return
    const kept = rules.value.filter((r) => r.object_id !== n.object_id)
    const next = silenced ? [{ object_type: n.object_type, object_id: n.object_id, delivery: 'silent' as const }, ...kept] : kept
    settingsStore.putMeSettings({ notifications: { rules: next } })
  }

  return { notifications, connected, unread, getDelivery, getNotificationList, connect, disconnect, putRead, putAllRead, deleteOne, isSilenced, putSilenced }
})
